import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

export default function QuestionnaireSection({ questionnaires }) {
  const categorizedQuestionnaires = {
    "Physical Metrics": questionnaires.filter(
      (q) =>
        q.question.includes("weight") ||
        q.question.includes("tall") ||
        q.question.includes("heart rate") ||
        q.question.includes("blood pressure")
    ),
    "Medical History": questionnaires.filter(
      (q) =>
        q.question.includes("conditions") ||
        q.question.includes("diagnosed") ||
        q.question.includes("history") ||
        q.question.includes("allergic") ||
        q.question.includes("medications")
    ),
    "Mental Health": questionnaires.filter(
      (q) =>
        q.question.includes("Over the last 2 weeks") ||
        q.question.includes("suicide")
    ),
    Lifestyle: questionnaires.filter(
      (q) =>
        q.question.includes("struggle with") ||
        q.question.includes("tried in the past") ||
        q.question.includes("alcohol")
    ),
  };

  // Initialize accordion state - all open by default
  const [accordionState, setAccordionState] = useState({
    "Physical Metrics": true,
    "Medical History": true,
    "Mental Health": true,
    Lifestyle: true,
  });

  // Toggle accordion state
  const toggleAccordion = (category) => {
    setAccordionState({
      ...accordionState,
      [category]: !accordionState[category],
    });
  };

  // Function to get appropriate badge color based on answer content
  const getBadgeColor = (answer) => {
    const lowerAnswer = answer?.toLowerCase();

    if (
      lowerAnswer.includes("no") ||
      lowerAnswer === "not at all" ||
      lowerAnswer === "unknown"
    ) {
      return "bg-gray-300 text-gray-800";
    } else if (
      lowerAnswer.includes("yes") ||
      lowerAnswer.includes("several days") ||
      lowerAnswer.includes("more than half")
    ) {
      return "bg-orange-200 text-orange-800";
    } else if (
      lowerAnswer.includes("diabetes") ||
      lowerAnswer.includes("15 or more") ||
      lowerAnswer.includes("suicide") ||
      lowerAnswer.includes("neoplasia")
    ) {
      return "bg-red-100 text-red-800";
    }

    return "bg-blue-100 text-blue-800";
  };

  return (
    <div className="bg-white mt-8 border p-6 rounded-md">
      <div className="space-y-4">
        {Object.entries(categorizedQuestionnaires).map(([category, items]) => (
          <div
            key={category}
            className="border border-gray-200 rounded-lg overflow-hidden"
          >
            {/* Accordion Header */}
            <div
              className="bg-gray-50 p-4 flex justify-between items-center cursor-pointer"
              onClick={() => toggleAccordion(category)}
            >
              <h3 className="text-lg font-semibold text-gray-700">
                {category}
              </h3>
              <div className="text-gray-500">
                {accordionState[category] ? (
                  <ChevronUp size={20} />
                ) : (
                  <ChevronDown size={20} />
                )}
              </div>
            </div>

            {/* Accordion Content */}
            {accordionState[category] && (
              <div className="p-4 bg-white">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {items.map((item, index) => (
                    <div
                      key={index}
                      className="bg-gray-50 rounded-md p-3 shadow-sm"
                    >
                      <p className="text-sm text-gray-700 mb-1">
                        {item.question}
                      </p>
                      <div className="flex items-center">
                        {item.answer && (
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${getBadgeColor(
                              item.answer
                            )}`}
                          >
                            {item.answer}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="flex flex-wrap items-center">
          <div className="flex items-center mr-4 mb-2">
            <div className="w-3 h-3 rounded-full bg-red-100 mr-2"></div>
            <span className="text-xs text-gray-500">Requires attention</span>
          </div>

          <div className="flex items-center mr-4 mb-2">
            <div className="w-3 h-3 rounded-full bg-orange-200 mr-2"></div>
            <span className="text-xs text-gray-500">Monitor</span>
          </div>

          <div className="flex items-center mr-4 mb-2">
            <div className="w-3 h-3 rounded-full bg-blue-100 mr-2"></div>
            <span className="text-xs text-gray-500">Normal</span>
          </div>

          <div className="flex items-center mb-2">
            <div className="w-3 h-3 rounded-full bg-gray-300 mr-2"></div>
            <span className="text-xs text-gray-500">No data/Negative</span>
          </div>
        </div>
      </div>
    </div>
  );
}
